import { useEffect, useRef, useState } from "react";
import { useTimezoneStore } from "@/shared/stores/useTimezoneStore.ts";
import type { TLocation } from "@/locations/types/location.ts";

/**
 * Sites that open on a specific machine rather than whichever one happens to be
 * online first, keyed by location name → machine name.
 *
 * Casa de Pedra has two machines whose names sort adjacently (ES5503,
 * ES5503-2), and the operators there work the second one; opening on ES5503
 * meant a manual switch on every visit.
 *
 * A stopgap, and deliberately a small legible one: the durable fix is a
 * per-location default on the backend (it already serves per-location settings),
 * at which point this map is deleted rather than grown. Matching by NAME rather
 * than id keeps it readable and survives the id differing per environment —
 * a rename silently falls back to the normal rule, which is the safe direction.
 */
const PREFERRED_DEFAULT_MACHINE: Readonly<Record<string, string>> = Object.freeze({
  "Casa de Pedra": "ES5503-2",
});

/**
 * The machine a location opens on — its preferred one if it has one, else its
 * first online one, else its first. Every location change routes through this so
 * switching location lands on a populated review layout (timelines + video)
 * instead of an empty one the operator has to click a machine out of. `null` for
 * a location with no machines.
 *
 * A preferred machine wins even when OFFLINE: it is the one that site works, and
 * silently opening a different machine because the preferred one is briefly down
 * is exactly the surprise this removes.
 */
export const defaultMachineIdAt = (
  location?: {
    name?: string;
    machines?: { id: number; name?: string; status?: string }[];
  } | null,
) => {
  const machines = location?.machines ?? [];
  const preferredName = location?.name ? PREFERRED_DEFAULT_MACHINE[location.name] : undefined;
  const preferred = preferredName
    ? machines.find((m) => m.name === preferredName)
    : undefined;
  return (preferred ?? machines.find((m) => m.status === "online") ?? machines[0])?.id ?? null;
};

interface UseMapSelectionArgs {
  locations: TLocation[];
  organizationId: number | null;
  /**
   * Cross-layer cleanup for an org switch — the alert card, event card, seek
   * target, track list, overlay version and polygon selection all belong to the
   * previous org. Injected rather than reached into from here: this hook owns
   * WHICH location/machine is selected, not what the rest of the page shows.
   */
  onOrgChange: () => void;
}

/**
 * What the map is currently looking at: organization overview vs a location, and
 * which machine within it.
 *
 * Owns the selection and the rules that keep it VALID. The handlers that act on
 * a selection change — resetting panels, clearing cards, prompting about queued
 * timeline edits — stay in the page, because they coordinate across layers.
 */
export const useMapSelection = ({
  locations,
  organizationId,
  onOrgChange,
}: UseMapSelectionArgs) => {
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
  const [view, setView] = useState<"org" | "location">("org");

  // Publish the viewed location so every rendered time resolves to its zone.
  const setActiveTimezoneLocation = useTimezoneStore((s) => s.setActiveLocation);
  useEffect(() => {
    setActiveTimezoneLocation(selectedLocationId);
    return () => setActiveTimezoneLocation(null);
  }, [selectedLocationId, setActiveTimezoneLocation]);

  // Drop straight into the live review layout whenever the current selection
  // doesn't name a location in the loaded list: on first load (nothing selected)
  // AND after an organization switch, where the locations query re-keys and the
  // previous org's location id no longer resolves. Without this the page keeps a
  // dead id, `selectedLocation` stays null, and the rail, markers, timelines and
  // video all render empty until the operator picks a location by hand.
  //
  // It deliberately keys off validity rather than a one-shot "did auto-select"
  // flag: a manual pick is always a valid location, so this leaves it alone —
  // including the deselected-machine and back-to-org states, which keep their
  // location id.
  useEffect(() => {
    if (locations.length === 0) return;
    if (locations.some((l) => l.id === selectedLocationId)) return;
    // Prefer a location that actually has machines, so the stacked timelines +
    // docked video show immediately in live mode.
    const location = locations.find((l) => (l.machines?.length ?? 0) > 0) ?? locations[0];
    setSelectedLocationId(location.id);
    setView("location");
    setSelectedMachineId(defaultMachineIdAt(location));
  }, [locations, selectedLocationId]);

  // An org switch re-keys every map query, so nothing selected under the old org
  // survives it. Drop the whole selection eagerly rather than waiting for the
  // reconciler above to notice the id has stopped resolving: the new org's
  // locations take a round-trip to arrive, and until they do the old id is still
  // handed to the live-alerts subscription, the timezone store and the overlay
  // — i.e. the previous org's location. The reconciler then re-picks a default
  // as soon as the new list lands.
  const onOrgChangeRef = useRef(onOrgChange);
  onOrgChangeRef.current = onOrgChange;
  const selectionOrgRef = useRef(organizationId);
  useEffect(() => {
    if (selectionOrgRef.current === organizationId) return;
    selectionOrgRef.current = organizationId;
    setSelectedLocationId(null);
    setSelectedMachineId(null);
    setView("org");
    onOrgChangeRef.current();
  }, [organizationId]);

  return {
    selectedLocationId,
    setSelectedLocationId,
    selectedMachineId,
    setSelectedMachineId,
    view,
    setView,
  };
};
